import { getUrlPrefix } from '~/lang/locales'
import {
  pricingCalendarResponseSchema,
  type PricingCalendarResponse,
} from './schemas'
import { PRICING_CALENDAR_ENDPOINT } from './config'
import { CalendarFetchError, CalendarValidationError } from './CalendarError'

// Default timeout for pricing requests (45 seconds - heavier endpoint)
const PRICING_TIMEOUT = 45000

export class PricingCalendarRepositoryFetch {
  /**
   * Fetch pricing calendar data from Calendar API v2
   * This endpoint provides exact prices for a specific duration (nights parameter required)
   */
  async get(
    langCode: string,
    params: Record<string, any>, // Already mapped API parameters
    signal?: AbortSignal,
  ): Promise<PricingCalendarResponse> {
    // Build query parameters
    const queryParams = new URLSearchParams()

    // Build query string from API parameters
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.set(key, String(value))
      }
    })

    const urlPrefix = getUrlPrefix(langCode)
    const url = `/_/fallback${urlPrefix}${PRICING_CALENDAR_ENDPOINT}?${queryParams.toString()}`

    // Separate error handling for fetch vs validation
    let response: any

    // Step 1: Fetch the data
    try {
      response = await $fetch<PricingCalendarResponse>(url, {
        signal,
        timeout: PRICING_TIMEOUT,
      })
    } catch (error: any) {
      // Silently return empty response for abort errors - they're intentional cancellations
      if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
        return { days: [] } as PricingCalendarResponse
      }
      const calendarError = new CalendarFetchError(
        'pricing',
        url,
        params,
        error,
      )
      // Don't log here - let the store handle logging to avoid duplicates
      throw calendarError
    }

    // Step 2: Validate with Zod schema
    try {
      return pricingCalendarResponseSchema.parse(response)
    } catch (error: any) {
      const validationError = new CalendarValidationError(
        'pricing',
        url,
        params,
        response,
        error,
      )
      // Don't log here - let the store handle logging to avoid duplicates
      throw validationError
    }
  }
}
